import React, { useState } from 'react';
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

const SERIES = [
    { key: 'views', label: 'Property views', color: '#55C5D0', type: 'area' },
    { key: 'unique_buyers', label: 'Unique buyers', color: '#3a99a3', type: 'area' },
    { key: 'enquiries', label: 'Enquiries', color: '#10b981', type: 'bar' },
    { key: 'saves', label: 'Saves', color: '#f472b6', type: 'bar' },
    { key: 'offers', label: 'Offers', color: '#8b5cf6', type: 'bar' },
    { key: 'ofi_registrations', label: 'Open home registrations', color: '#f59e0b', type: 'bar' },
];

function formatDay(value) {
    const date = new Date(value);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/**
 * Engagement over time. One series at a time keeps the shape readable, which is
 * the whole point: is interest rising, flat or falling.
 */
export default function EngagementCharts({ data = [] }) {
    const [active, setActive] = useState('views');
    const series = SERIES.find((s) => s.key === active) || SERIES[0];
    const total = data.reduce((sum, row) => sum + (row[series.key] || 0), 0);

    if (!data.length) {
        return (
            <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                Not enough activity to chart yet.
            </p>
        );
    }

    return (
        <div>
            <div className="mb-3 flex flex-wrap gap-1.5">
                {SERIES.map((option) => (
                    <button
                        key={option.key}
                        type="button"
                        onClick={() => setActive(option.key)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                            active === option.key
                                ? 'bg-[#374151] text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            <div className="mb-2 text-xs font-semibold text-gray-500">
                {total.toLocaleString()} {series.label.toLowerCase()} over {data.length} days
            </div>

            <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    {series.type === 'area' ? (
                        <AreaChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                            <defs>
                                <linearGradient id={`grad-${series.key}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={series.color} stopOpacity={0.45} />
                                    <stop offset="100%" stopColor={series.color} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" vertical={false} />
                            <XAxis dataKey="date" tickFormatter={formatDay} tick={{ fontSize: 11, fill: '#9ca3af' }}
                                   axisLine={false} tickLine={false} minTickGap={24} />
                            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip
                                labelFormatter={formatDay}
                                formatter={(value) => [value, series.label]}
                                contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12 }}
                            />
                            <Area type="monotone" dataKey={series.key} stroke={series.color} strokeWidth={2}
                                  fill={`url(#grad-${series.key})`} />
                        </AreaChart>
                    ) : (
                        <BarChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f5" vertical={false} />
                            <XAxis dataKey="date" tickFormatter={formatDay} tick={{ fontSize: 11, fill: '#9ca3af' }}
                                   axisLine={false} tickLine={false} minTickGap={24} />
                            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip
                                labelFormatter={formatDay}
                                formatter={(value) => [value, series.label]}
                                contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12 }}
                            />
                            <Bar dataKey={series.key} fill={series.color} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    )}
                </ResponsiveContainer>
            </div>
        </div>
    );
}
